import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getDocs, collection } from 'firebase/firestore';
import { auth, db } from './firebase';
import RSVPPage from './components/RSVPPage';
import ParentalConsentModal from './components/ParentalConsentModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import TermsModal from './components/TermsModal';
import LegalPage from './components/LegalPage';
import './App.css';

// Admin-only routes are split into their own chunks so the public page doesn't
// download them. AdminPage pulls in xlsx (SheetJS, ~860 KB minified) for the SMS
// log export, and it was reaching every visitor: splitting these three took the
// public bundle from 341 kB to 211 kB gzipped. These must stay lazy — a plain
// import here silently undoes it.
//
// They sit below the imports rather than beside them because eslint's
// import/first rule rejects an import that follows a const.
const AdminPage = lazy(() => import('./components/AdminPage'));
const MakeTeamsPage = lazy(() => import('./components/MakeTeamsPage'));
const SmsTestPage = lazy(() => import('./components/SmsTestPage'));

function App() {
  // adminUser is tracked here only to toggle the footer link label (⚙ Admin vs Admin).
  // All real auth checks happen inside AdminPage.js independently.
  const [adminUser, setAdminUser] = useState(null);
  const [footerContent, setFooterContent] = useState('');
  const [heroImageCss, setHeroImageCss] = useState('');
  const [showConsent, setShowConsent] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setAdminUser(user || null);
    });

    getDocs(collection(db, 'content')).then(snap => {
      if (!snap.empty) {
        const data = snap.docs[0].data();
        setFooterContent(data.footer || '');
        setHeroImageCss(data.heroImageCss || '');
      }
    }).catch(console.error);

    // Load favicon from settings
    getDocs(collection(db, 'settings')).then(snap => {
      if (!snap.empty) {
        const s = snap.docs[0].data();
        if (s.faviconUrl) setFaviconUrl(s.faviconUrl);
      }
    }).catch(console.error);

    return () => unsubscribe();
  }, []);

  // Apply favicon dynamically
  useEffect(() => {
    if (!faviconUrl) return;
    if (window.location.pathname === '/make-teams') return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  return (
    <Router>
      <div className="App">
        {heroImageCss && (
          <style>{`.hero-image { ${heroImageCss} }`}</style>
        )}
        {/* Fallback only shows while an admin chunk downloads; "/" is not lazy. */}
        <Suspense fallback={<div style={{padding:'2rem', textAlign:'center', color:'var(--slate)'}}>Loading…</div>}>
          <Routes>
            <Route path="/" element={<RSVPPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/make-teams" element={<MakeTeamsPage />} />
            <Route path="/sms-test" element={<SmsTestPage />} />
            {/* Linkable policy pages — cited by URL in the toll-free SMS filing.
                Same content as the footer modals; see LegalPage.js. */}
            <Route path="/privacy" element={<LegalPage doc="privacy" />} />
            <Route path="/terms" element={<LegalPage doc="terms" />} />
          </Routes>
        </Suspense>

        <footer className="footer">
          {footerContent ? (
            <div className="footer-content"
              dangerouslySetInnerHTML={{ __html: footerContent }} />
          ) : null}
          <div className="footer-links">
            <button className="footer-consent-btn"
              onClick={() => setShowConsent(true)}>
              Parental<br/>Consent
            </button>
            <button className="footer-consent-btn"
              onClick={() => setShowPrivacy(true)}>
              Privacy<br/>Policy
            </button>
            <button className="footer-consent-btn"
              onClick={() => setShowTerms(true)}>
              Terms of<br/>Service
            </button>
            <Link to="/admin" className="footer-admin-link">
              {adminUser ? '⚙ Admin' : 'Admin'}<br/>
              <span style={{fontSize:'9px', opacity:0.6, letterSpacing:'0.5px'}}>v.99</span>
            </Link>
          </div>
        </footer>

        {showConsent && (
          <ParentalConsentModal onClose={() => setShowConsent(false)} />
        )}
        {showPrivacy && (
          <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />
        )}
        {showTerms && (
          <TermsModal onClose={() => setShowTerms(false)} />
        )}
      </div>
    </Router>
  );
}

export default App;
